/**
 * Restaurant Booking Page
 */

import { Header, renderServiceTabs } from '../components';
import { setState, navigateTo } from '../store';
import { getRestaurantConfig } from '../api';
import { getCurrentLanguage } from '../i18n';

interface LocalizedString { th: string; en: string; zh: string; }
interface MealPeriod {
  id: string;
  name: LocalizedString | string;
  slots: string[];
  timeSlots?: string[];
  maxPaxPerSlot?: number;
}
interface OutletImage {
  url: string;
  thumbUrl?: string;
}
interface RestaurantOutletData {
  id?: string;
  name?: LocalizedString | string;
  description?: LocalizedString | string;
  enabled?: boolean;
  openTime?: string;
  closeTime?: string;
  images?: OutletImage[];
  mealPeriods?: MealPeriod[];
  outlets?: RestaurantOutletData[];
}

let restaurantOutlets: RestaurantOutletData[] = [];
let activeOutletId: string | null = null;
let restConfigLoaded = false;

let MEAL_PERIODS: MealPeriod[] = [
  { id: 'breakfast', name: { th: 'อาหารเช้า (Breakfast)', en: 'Breakfast', zh: '早餐' }, slots: ['07:00', '08:00', '09:00', '10:00'] },
  { id: 'lunch',     name: { th: 'อาหารกลางวัน (Lunch)', en: 'Lunch', zh: '午餐' }, slots: ['11:30', '12:00', '12:30', '13:00', '13:30'] },
  { id: 'dinner',    name: { th: 'อาหารเย็น (Dinner)', en: 'Dinner', zh: '晚餐' }, slots: ['18:00', '18:30', '19:00', '19:30', '20:00'] },
];

const SEATING_AREAS = [
  { id: 'Indoor (แอร์)', name: { th: 'Indoor (แอร์)', en: 'Indoor (A/C)', zh: '室内 (空调)' } },
  { id: 'Outdoor (ริมสระ)', name: { th: 'Outdoor (ริมสระ)', en: 'Outdoor (Poolside)', zh: '室外 (泳池边)' } },
  { id: 'VIP Room', name: { th: 'VIP Room', en: 'VIP Room', zh: 'VIP 贵宾房' } },
];

let selectedMealId = 'dinner';
let selectedSeating = 'Outdoor (ริมสระ)';
let selectedDateStr = new Date().toISOString().split('T')[0];
let selectedTimeSlot = '19:00';
let partySizeCount = 4;
let dietaryInput = '';
let roomNumberInput = '';

function getLocalizedText(val: any, lang: string, fallback: string = ''): string {
  if (!val) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'object') {
    return val[lang] || val.th || val.en || val.zh || fallback;
  }
  return fallback;
}

export function renderRestaurantPage(): string {
  const lang = getCurrentLanguage();
  const activeOutlet = restaurantOutlets.find(o => o.id === activeOutletId) || restaurantOutlets[0] || {};
  const currentMealPeriods = activeOutlet.mealPeriods || MEAL_PERIODS;
  const outletImages = activeOutlet.images || [];

  const dates = [];
  const today = new Date();
  for (let i = 0; i < 7; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const iso = d.toISOString().split('T')[0];
    const dayLabel = d.toLocaleDateString(lang === 'th' ? 'th-TH' : lang === 'zh' ? 'zh-CN' : 'en-US', { weekday: 'short', day: 'numeric', month: 'short' });
    dates.push({ iso, label: dayLabel });
  }

  const currentMeal = currentMealPeriods.find(m => m.id === selectedMealId) || currentMealPeriods[0] || MEAL_PERIODS[2];
  const restNameText = getLocalizedText(activeOutlet.name, lang, 'ร้านอาหาร (Restaurant)');
  const restDescText = getLocalizedText(activeOutlet.description, lang, '');
  const openHoursText = activeOutlet.openTime && activeOutlet.closeTime ? `${activeOutlet.openTime} – ${activeOutlet.closeTime}` : '06:30 – 22:30';

  const uiText = {
    th: {
      mealTitle: '🍳 เลือกมื้ออาหาร (Meal Period)',
      seatingTitle: '🪑 เลือกโซนที่นั่ง (Seating Area)',
      dateTitle: '📅 เลือกรอบวันและเวลา',
      partyTitle: '👥 จำนวนที่นั่ง (Party Size)',
      partySub: 'จำนวนผู้รับประทานอาหาร',
      dietaryTitle: '🥗 ข้อจำกัดด้านอาหาร (Dietary Restrictions - Optional)',
      dietaryPlace: 'เช่น แพ้กุ้ง, ทานเจ, มังสวิรัติ',
      roomTitle: '🏨 หมายเลขห้องพัก (กรณีเป็นแขกพักในโรงแรม - Optional)',
      roomPlace: 'ระบุหมายเลขห้อง เช่น 1002',
      nextBtn: 'ถัดไป: กรอกข้อมูลผู้ใช้บริการ ›',
      openHours: '🕒 เวลาทำการ',
    },
    en: {
      mealTitle: '🍳 Select Meal Period',
      seatingTitle: '🪑 Select Seating Area',
      dateTitle: '📅 Select Date & Time',
      partyTitle: '👥 Party Size (Guests)',
      partySub: 'Number of guests for dining',
      dietaryTitle: '🥗 Dietary Restrictions (Optional)',
      dietaryPlace: 'e.g. Seafood allergy, Vegan, Vegetarian',
      roomTitle: '🏨 Room Number (For In-house Guests - Optional)',
      roomPlace: 'e.g. Room 1002',
      nextBtn: 'Next: Guest Details ›',
      openHours: '🕒 Operating Hours',
    },
    zh: {
      mealTitle: '🍳 选择用餐时间段',
      seatingTitle: '🪑 选择用餐区域',
      dateTitle: '📅 选择日期与时间',
      partyTitle: '👥 用餐人数',
      partySub: '用餐人数',
      dietaryTitle: '🥗 饮食忌口 (可选)',
      dietaryPlace: '例如: 海鲜过敏、素食',
      roomTitle: '🏨 房间号 (店内住客 - 可选)',
      roomPlace: '例如: 1002',
      nextBtn: '下一步：填写客人信息 ›',
      openHours: '🕒 营业时间',
    },
  }[lang] || {
    mealTitle: '🍳 Select Meal Period',
    seatingTitle: '🪑 Select Seating Area',
    dateTitle: '📅 Select Date & Time',
    partyTitle: '👥 Party Size (Guests)',
    partySub: 'Number of guests for dining',
    dietaryTitle: '🥗 Dietary Restrictions (Optional)',
    dietaryPlace: 'e.g. Seafood allergy, Vegan, Vegetarian',
    roomTitle: '🏨 Room Number (For In-house Guests - Optional)',
    roomPlace: 'e.g. Room 1002',
    nextBtn: 'Next: Guest Details ›',
    openHours: '🕒 Operating Hours',
  };

  return `
    ${Header(restNameText, false, true)}
    ${renderServiceTabs('restaurant')}

    <div class="page-container" style="padding:16px;padding-bottom:100px;">
      
      <!-- Multi-Outlet Selector Tabs (if > 1 outlet) -->
      ${restaurantOutlets.length > 1 ? `
        <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:8px;margin-bottom:12px;">
          ${restaurantOutlets.map(o => `
            <button
              onclick="window.selectRestOutlet?.('${o.id}')"
              style="padding:8px 14px;border-radius:20px;border:1px solid ${activeOutletId === o.id ? '#00B900' : '#e0e0e0'};background:${activeOutletId === o.id ? '#00B900' : '#fff'};color:${activeOutletId === o.id ? '#fff' : '#333'};font-size:13px;font-weight:600;white-space:nowrap;cursor:pointer;"
            >
              🍽️ ${getLocalizedText(o.name, lang, o.id || 'Restaurant')}
            </button>
          `).join('')}
        </div>
      ` : ''}

      <!-- Cover Image Banner & Gallery Slider -->
      ${outletImages.length > 0 ? `
        <div style="margin-bottom:16px;border-radius:12px;overflow:hidden;box-shadow:0 2px 6px rgba(0,0,0,0.1);background:#fff;">
          <img src="${outletImages[0].url || outletImages[0].thumbUrl}" alt="Restaurant Cover" style="width:100%;height:180px;object-fit:cover;display:block;" />
          ${outletImages.length > 1 ? `
            <div style="display:flex;gap:6px;padding:8px;overflow-x:auto;background:#fafafa;border-top:1px solid #f0f0f0;">
              ${outletImages.map(img => `
                <img src="${img.thumbUrl || img.url}" alt="" style="width:50px;height:50px;object-fit:cover;border-radius:6px;border:1px solid #e0e0e0;flex-shrink:0;" />
              `).join('')}
            </div>
          ` : ''}
        </div>
      ` : ''}

      <!-- Outlet Information Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h2 style="font-size:18px;font-weight:700;color:#1a1a1a;margin-bottom:4px;">${restNameText}</h2>
        ${restDescText ? `<p style="font-size:13px;color:#666;line-height:1.4;margin-bottom:10px;">${restDescText}</p>` : ''}
        <div style="display:flex;align-items:center;gap:8px;">
          <span style="display:inline-flex;align-items:center;padding:4px 10px;background:#E8F5E8;color:#00B900;border-radius:12px;font-size:12px;font-weight:600;">
            ${uiText.openHours}: ${openHoursText}
          </span>
        </div>
      </div>

      <!-- Meal Period Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${uiText.mealTitle}</h3>
        <div style="display:flex;gap:8px;">
          ${currentMealPeriods.map(m => {
            const mealTitle = getLocalizedText(m.name, lang, m.id);
            return `
              <button 
                onclick="window.selectMealPeriod?.('${m.id}')"
                style="flex:1;padding:10px 6px;border-radius:8px;border:1px solid ${selectedMealId === m.id ? '#00B900' : '#e0e0e0'};background:${selectedMealId === m.id ? '#E8F5E8' : '#fff'};color:${selectedMealId === m.id ? '#00B900' : '#333'};font-size:13px;font-weight:${selectedMealId === m.id ? '600' : '400'};cursor:pointer;"
              >
                ${mealTitle.split(' ')[0]}
              </button>
            `;
          }).join('')}
        </div>
      </div>

      <!-- Seating Area Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${uiText.seatingTitle}</h3>
        <div style="display:flex;gap:8px;">
          ${SEATING_AREAS.map(s => {
            const areaName = getLocalizedText(s.name, lang, s.id);
            return `
              <button 
                onclick="window.selectSeatingArea?.('${s.id}')"
                style="flex:1;padding:10px 6px;border-radius:8px;border:1.5px solid ${selectedSeating === s.id ? '#00B900' : '#e0e0e0'};background:${selectedSeating === s.id ? '#F9FDF9' : '#fff'};color:${selectedSeating === s.id ? '#00B900' : '#333'};font-size:13px;font-weight:${selectedSeating === s.id ? '600' : '400'};cursor:pointer;"
              >
                ${areaName}
              </button>
            `;
          }).join('')}
        </div>
      </div>

      <!-- Date & Time Slot Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <h3 style="font-size:16px;font-weight:600;margin-bottom:12px;color:#1a1a1a;">${uiText.dateTitle}</h3>
        
        <!-- Date Chips -->
        <div style="display:flex;gap:8px;overflow-x:auto;padding-bottom:8px;margin-bottom:12px;">
          ${dates.map(d => `
            <button 
              class="date-chip ${selectedDateStr === d.iso ? 'active' : ''}" 
              onclick="window.selectRestDate?.('${d.iso}')"
              style="padding:8px 12px;border-radius:8px;border:1px solid ${selectedDateStr === d.iso ? '#00B900' : '#e0e0e0'};background:${selectedDateStr === d.iso ? '#E8F5E8' : '#fff'};color:${selectedDateStr === d.iso ? '#00B900' : '#333'};font-size:13px;font-weight:${selectedDateStr === d.iso ? '600' : '400'};white-space:nowrap;cursor:pointer;"
            >
              ${d.label}
            </button>
          `).join('')}
        </div>

        <!-- Time Slot Chips -->
        <div style="display:flex;flex-wrap:wrap;gap:8px;">
          ${(currentMeal.slots || (currentMeal as any).timeSlots || []).map((slot: string) => `
            <button 
              class="time-chip ${selectedTimeSlot === slot ? 'active' : ''}"
              onclick="window.selectRestTimeSlot?.('${slot}')"
              style="padding:8px 14px;border-radius:20px;border:1px solid ${selectedTimeSlot === slot ? '#00B900' : '#e0e0e0'};background:${selectedTimeSlot === slot ? '#00B900' : '#fff'};color:${selectedTimeSlot === slot ? '#fff' : '#333'};font-size:13px;font-weight:600;cursor:pointer;"
            >
              ${slot}
            </button>
          `).join('')}
        </div>
      </div>

      <!-- Party Size & Dietary Restrictions Card -->
      <div class="card" style="background:#fff;border-radius:12px;padding:16px;margin-bottom:16px;box-shadow:0 1px 3px rgba(0,0,0,0.08);">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px;">
          <div>
            <div style="font-size:15px;font-weight:600;color:#1a1a1a;">${uiText.partyTitle}</div>
            <div style="font-size:12px;color:#666;">${uiText.partySub}</div>
          </div>
          <div style="display:flex;align-items:center;gap:12px;border:1px solid #e0e0e0;border-radius:8px;padding:4px 8px;">
            <button onclick="window.changePartySize?.(-1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">-</button>
            <span style="font-size:15px;font-weight:600;min-width:20px;text-align:center;">${partySizeCount}</span>
            <button onclick="window.changePartySize?.(1)" style="border:none;background:none;font-size:18px;font-weight:700;color:#00B900;width:28px;cursor:pointer;">+</button>
          </div>
        </div>

        <div style="border-top:1px solid #f0f0f0;padding-top:12px;margin-bottom:12px;">
          <label style="display:block;font-size:13px;font-weight:600;color:#333;margin-bottom:6px;">${uiText.dietaryTitle}</label>
          <input 
            type="text" 
            id="rest_dietary" 
            value="${dietaryInput}"
            onchange="window.updateRestDietary?.(this.value)"
            placeholder="${uiText.dietaryPlace}"
            style="width:100%;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px;outline:none;"
          />
        </div>

        <div style="border-top:1px solid #f0f0f0;padding-top:12px;">
          <label style="display:block;font-size:13px;font-weight:600;color:#333;margin-bottom:6px;">${uiText.roomTitle}</label>
          <input 
            type="text" 
            id="rest_room_number" 
            value="${roomNumberInput}"
            onchange="window.updateRestRoomNumber?.(this.value)"
            placeholder="${uiText.roomPlace}"
            style="width:100%;padding:10px 12px;border:1px solid #e0e0e0;border-radius:8px;font-size:14px;outline:none;"
          />
        </div>
      </div>

    </div>

    <!-- Fixed Bottom Action Bar -->
    <div style="position:fixed;bottom:0;left:0;right:0;max-width:480px;margin:0 auto;background:#fff;padding:12px 16px;border-top:1px solid #e0e0e0;box-shadow:0 -2px 10px rgba(0,0,0,0.05);z-index:100;display:flex;align-items:center;justify-content:space-between;">
      <div>
        <div style="font-size:12px;color:#666;">Pay at Venue</div>
        <div style="font-size:16px;font-weight:700;color:#00B900;">FREE Reservation</div>
      </div>
      <button 
        onclick="window.proceedRestToGuestInfo?.()"
        style="padding:12px 20px;background:#00B900;color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:600;cursor:pointer;display:flex;align-items:center;gap:6px;"
      >
        ${uiText.nextBtn}
      </button>
    </div>
  `;
}

export function initRestaurantPage(): void {
  if (!restConfigLoaded) {
    restConfigLoaded = true;
    getRestaurantConfig().then((cfg: any) => {
      if (cfg) {
        if (cfg.outlets && cfg.outlets.length > 0) {
          restaurantOutlets = cfg.outlets;
          activeOutletId = restaurantOutlets[0].id || null;
        } else if (cfg.id) {
          restaurantOutlets = [cfg];
          activeOutletId = cfg.id || 'restaurant_main';
        } else {
          restaurantOutlets = [];
          activeOutletId = null;
        }
        const active = restaurantOutlets.find(o => o.id === activeOutletId) || restaurantOutlets[0];
        if (active && active.mealPeriods && active.mealPeriods.length > 0) {
          MEAL_PERIODS = active.mealPeriods.map((mp: any) => ({
            id: mp.id,
            name: mp.name,
            slots: mp.timeSlots && mp.timeSlots.length > 0 ? mp.timeSlots : (mp.slots || ['12:00', '18:00']),
          }));
          if (!MEAL_PERIODS.some((m) => m.id === selectedMealId)) {
            selectedMealId = MEAL_PERIODS[0].id;
          }
          const m = MEAL_PERIODS.find((meal) => meal.id === selectedMealId);
          if (m && m.slots && m.slots.length > 0) {
            selectedTimeSlot = m.slots[0];
          }
        }
        (window as any).renderApp?.();
      }
    }).catch(err => console.error('[RestaurantPage] Error fetching dynamic config:', err));
  }

  (window as any).selectRestOutlet = (outletId: string) => {
    activeOutletId = outletId;
    const active = restaurantOutlets.find(o => o.id === outletId);
    if (active && active.mealPeriods && active.mealPeriods.length > 0) {
      MEAL_PERIODS = active.mealPeriods.map((mp: any) => ({
        id: mp.id,
        name: mp.name,
        slots: mp.timeSlots && mp.timeSlots.length > 0 ? mp.timeSlots : (mp.slots || ['12:00', '18:00']),
      }));
      if (!MEAL_PERIODS.some((m) => m.id === selectedMealId)) {
        selectedMealId = MEAL_PERIODS[0].id;
      }
      const m = MEAL_PERIODS.find((meal) => meal.id === selectedMealId);
      if (m && m.slots && m.slots.length > 0) {
        selectedTimeSlot = m.slots[0];
      }
    }
    (window as any).renderApp?.();
  };

  (window as any).selectMealPeriod = (id: string) => {
    selectedMealId = id;
    const active = restaurantOutlets.find(o => o.id === activeOutletId) || restaurantOutlets[0];
    const periods = active?.mealPeriods || MEAL_PERIODS;
    const meal = periods.find((m: any) => m.id === id);
    const slots = meal?.timeSlots || meal?.slots || ['18:00'];
    if (slots.length > 0) {
      selectedTimeSlot = slots[0];
    }
    (window as any).renderApp?.();
  };

  (window as any).selectSeatingArea = (area: string) => {
    selectedSeating = area;
    (window as any).renderApp?.();
  };

  (window as any).selectRestDate = (iso: string) => {
    selectedDateStr = iso;
    (window as any).renderApp?.();
  };

  (window as any).selectRestTimeSlot = (slot: string) => {
    selectedTimeSlot = slot;
    (window as any).renderApp?.();
  };

  (window as any).changePartySize = (delta: number) => {
    partySizeCount = Math.max(1, Math.min(20, partySizeCount + delta));
    (window as any).renderApp?.();
  };

  (window as any).updateRestDietary = (val: string) => {
    dietaryInput = val;
  };

  (window as any).updateRestRoomNumber = (val: string) => {
    roomNumberInput = val;
  };

  (window as any).proceedRestToGuestInfo = () => {
    const lang = getCurrentLanguage();
    const dEl = document.getElementById('rest_dietary') as HTMLInputElement;
    if (dEl) dietaryInput = dEl.value;
    const rEl = document.getElementById('rest_room_number') as HTMLInputElement;
    if (rEl) roomNumberInput = rEl.value;

    const activeOutlet = restaurantOutlets.find(o => o.id === activeOutletId) || restaurantOutlets[0] || {};
    const periods = activeOutlet.mealPeriods || MEAL_PERIODS;
    const meal = periods.find((m: any) => m.id === selectedMealId) || periods[0] || MEAL_PERIODS[2];
    const mealNameStr = getLocalizedText(meal.name, lang, meal.id);

    setState({
      bookingType: 'RESTAURANT',
      restaurantDetails: {
        bookingDate: selectedDateStr,
        timeSlot: selectedTimeSlot,
        partySize: partySizeCount,
        seatingArea: selectedSeating,
        dietaryRestrictions: dietaryInput,
        roomNumber: roomNumberInput,
        inHouseGuestName: roomNumberInput ? `ห้อง ${roomNumberInput}` : undefined,
      },
      selectedRoom: {
        roomTypeCode: 'RESTAURANT_TABLE',
        roomTypeName: `🍽️ จองโต๊ะอาหาร (${selectedSeating})`,
        ratePlanCode: 'PAY_AT_VENUE',
        ratePlanName: 'ชำระเงินที่ร้าน (Pay at Restaurant)',
        totalRate: 0,
        ratePerRoom: 0,
        averageNightlyRate: 0,
        totalBeforeTax: 0,
        numberOfRooms: 1,
        currency: 'THB',
        available: 1,
        description: `มื้อ${mealNameStr} รอบเวลา ${selectedTimeSlot} น. สำหรับ ${partySizeCount} ท่าน`,
      },
    });

    navigateTo('guest-info');
  };
}
